import {Inject} from '@angular/core'
import {FILE_READER} from './fileReader.provider'
import {take} from 'rxjs/operators'
import {Observable, ReplaySubject, Subject} from 'rxjs'


export interface IFileUpload {

  /**
   * The name of the file
   */
  name: string

  /**
   * The content type, we will possibly use this
   * later.
   */
  contentType: string

  /**
   * Content is a base64 encoded string bc that is what borgo wants?
   */
  content: string

  /**
   * The id as received from the server
   */
  id?: string

  /**
   * Size
   */
  size: number
}

/**
 * Abstract the file uploading so that you can upload
 * images from here and there.
 *
 * Please review if it can be used in Product Database (import)
 * and other places where we upload "binary" objects.
 *
 */
export class Uploader {
  /**
   * This is where the subclasses get a stream of file
   * objects.
   */
  protected result$: Observable<IFileUpload>

  /**
   * This is the complete file uploaded with all metadata.
   *
   */
  private fileData$ = new Subject<IFileUpload>()

  /**
   * Here we publish the metadata so that the file reader
   * can "remember" that.
   */
  private metaData$ = new ReplaySubject<File>(1)

  constructor(
    @Inject(FILE_READER) private fileReader: FileReader
  ) {
    // Set the callback for the data is read complete
    this.fileReader.onloadend = this.onLoadEnd

    // Let the result be the observable of our subject
    this.result$ = this.fileData$.asObservable()
  }

  public onFileSelected(event: EventTarget | null): void {
    const selectedFiles: FileList = (event as HTMLInputElement).files as FileList
    if (selectedFiles) {
      const files = Array.from(selectedFiles)
      this.processFile(files)
      this.fileData$.pipe(
        take(selectedFiles.length - 1)
      ).subscribe({
        next: () => {
          this.processFile(files)
        }
      })
    }
  }

  private processFile(files: File[]): void {
    const file = files.shift() as File
    this.metaData$.next(file)
    this.fileReader.readAsArrayBuffer(file as File)
  }

  private onLoadEnd = (): any => {
    const result = this.fileReader.result as ArrayBuffer
    this.metaData$.pipe(
      take(1)
    ).subscribe({
      next: (file: File) => {
        // Convert to a string? Potentially huge one! Works with 1.5 mb, if
        // file.size > 1 MB abort and set an error message anyways
        const base64String = btoa(new Uint8Array(result).reduce((data, byte) => data + String.fromCharCode(byte), ''))
        this.fileData$.next(
          {size: result.byteLength, name: file.name, contentType: file.type, content: base64String})
      }
    })

  }
}
